import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ButtonSpinner from '../../components/ui/ButtonSpinner';

const initialFieldErrors = {
  firstName: '',
  middleInitial: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

export const Signup = () => {
  const [firstName, setFirstName] = useState('');
  const [middleInitial, setMiddleInitial] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState(initialFieldErrors);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || !user || !role) return;
    if (role === 'client') navigate('/client/dashboard', { replace: true });
    else if (role === 'advisor') navigate('/advisor/dashboard', { replace: true });
    else if (role === 'admin') navigate('/admin/dashboard', { replace: true });
  }, [authLoading, user, role, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
        <div
          className="w-10 h-10 border-2 border-brand-navy border-t-transparent rounded-full animate-spin"
          aria-hidden
        />
        <p className="text-sm text-slate-600">Loading...</p>
      </div>
    );
  }

  const validate = () => {
    const err = { ...initialFieldErrors };
    if (!firstName.trim()) err.firstName = 'First name is required.';
    if (!lastName.trim()) err.lastName = 'Last name is required.';
    if (!email.trim()) err.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      err.email = 'Enter a valid email address.';
    }
    if (!password) err.password = 'Password is required.';
    else if (password.length < 8) err.password = 'Password must be at least 8 characters.';
    if (!confirmPassword) err.confirmPassword = 'Please confirm your password.';
    else if (confirmPassword !== password) err.confirmPassword = 'Passwords do not match.';
    const mi = middleInitial.trim();
    if (mi.length > 1) err.middleInitial = 'Middle initial must be a single character.';
    setFieldErrors(err);
    return !Object.values(err).some(Boolean);
  };

  const buildFullName = () => {
    const f = firstName.trim();
    const l = lastName.trim();
    const mi = middleInitial.trim();
    if (mi) return `${f} ${mi.toUpperCase()}. ${l}`;
    return `${f} ${l}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setLoading(true);
    try {
      const full_name = buildFullName();
      const { error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: { full_name },
        },
      });
      if (signUpError) {
        setError(signUpError.message);
        return;
      }
      await supabase.auth.signOut();
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleMiddleChange = (e) => {
    const v = e.target.value.slice(0, 1);
    setMiddleInitial(v ? v.toUpperCase() : '');
    setFieldErrors((prev) => ({ ...prev, middleInitial: '' }));
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-3xl font-bold text-brand-navy">
            <SafeIcon icon={FiIcons.FiBookOpen} className="text-brand-gold" />
            Libr Legacy
          </Link>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-slate-200">
            <div className="text-center space-y-6">
              <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto border border-emerald-100">
                <SafeIcon icon={FiIcons.FiMail} className="text-2xl text-emerald-600" />
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Check your email to confirm your account before signing in.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full btn-primary py-3"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-3xl font-bold text-brand-navy">
          <SafeIcon icon={FiIcons.FiBookOpen} className="text-brand-gold" />
          Libr Legacy
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-slate-200">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700">First name</label>
              <div className="mt-1">
                <input
                  type="text"
                  autoComplete="given-name"
                  className="input-field"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setFieldErrors((p) => ({ ...p, firstName: '' }));
                  }}
                  disabled={loading}
                />
              </div>
              {fieldErrors.firstName && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Middle initial <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  maxLength={1}
                  autoComplete="additional-name"
                  className="input-field max-w-[4rem]"
                  value={middleInitial}
                  onChange={handleMiddleChange}
                  disabled={loading}
                />
              </div>
              {fieldErrors.middleInitial && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.middleInitial}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Last name</label>
              <div className="mt-1">
                <input
                  type="text"
                  autoComplete="family-name"
                  className="input-field"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setFieldErrors((p) => ({ ...p, lastName: '' }));
                  }}
                  disabled={loading}
                />
              </div>
              {fieldErrors.lastName && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.lastName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email address</label>
              <div className="mt-1">
                <input
                  type="email"
                  autoComplete="email"
                  className="input-field"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setFieldErrors((p) => ({ ...p, email: '' }));
                  }}
                  disabled={loading}
                />
              </div>
              {fieldErrors.email && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="mt-1">
                <input
                  type="password"
                  autoComplete="new-password"
                  className="input-field"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setFieldErrors((p) => ({ ...p, password: '' }));
                  }}
                  disabled={loading}
                />
              </div>
              {fieldErrors.password && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm password</label>
              <div className="mt-1">
                <input
                  type="password"
                  autoComplete="new-password"
                  className="input-field"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setFieldErrors((p) => ({ ...p, confirmPassword: '' }));
                  }}
                  disabled={loading}
                />
              </div>
              {fieldErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
              )}
            </div>

            <button type="submit" disabled={loading} className="w-full btn-primary py-3">
              {loading ? (
                <>
                  <ButtonSpinner />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </button>
          </form>

          {error && (
            <div className="mt-4 bg-red-50 text-red-600 p-3 rounded-md text-sm flex items-start gap-2">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-brand-navy hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
